import { useDeviceType, useToast } from '@cian/ui-kit';
import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EDeviceType } from '../../../types/deviceType';
import { ETelemetryEvents } from '../../../types/telemetry';
import { useApplicationContext } from '../../../utils/applicationContext';
import { closeEventChannel, openEventChannel } from '../../actions/socket';
import { Notification } from '../../components/Notification';
import { selectIsListenNotificationExperimentEnabled } from '../../selectors/experiments/selectIsListenNotificationExperimentEnabled';
import { clearNotifications, selectNotifications } from '../../slices/notifications';
import { selectSessionToken } from '../../slices/sessionSlice';
import { selectUser } from '../../slices/userSlice';

export function NotificationsContainer() {
  const { config, custom, telemetry } = useApplicationContext();
  // Notes: useDeviceType not working
  const device = useDeviceType();
  const isMobile = custom.realDevice ? custom.realDevice !== 'desktop' : device !== EDeviceType.Desktop;

  const dispatch = useDispatch();
  const toastApi = useToast({
    placement: isMobile ? 'top-center' : 'top-right',
    maxToastsNumber: 10,
  });
  const notifications = useSelector(selectNotifications);
  const user = useSelector(selectUser);
  const token = useSelector(selectSessionToken);
  const isExperimentEnabled = useSelector(selectIsListenNotificationExperimentEnabled);
  const isListenNotificationEnabled = config.get<boolean>('frontend-chats.listenNotification.Enabled') || false;

  const isWSEnabled = React.useMemo(() => {
    return isListenNotificationEnabled && isExperimentEnabled && user.isAuthenticated && token;
  }, [isListenNotificationEnabled, isExperimentEnabled, user.isAuthenticated, token]);

  useEffect(() => {
    if (!user.isAuthenticated || !token || !isListenNotificationEnabled || !isExperimentEnabled) {
      return;
    }
    if (document.visibilityState !== 'hidden') {
      dispatch(openEventChannel());
    }
  }, []);

  useEffect(() => {
    if (!notifications.length) {
      return;
    }
    notifications.forEach(({ imageUrl, isOffer, message, title, id, chatId }) => {
      telemetry.increment(ETelemetryEvents.ChatNotificationShow);

      const notificationId = toastApi.open({
        dataTestId: `notification-${id}`,
        type: 'info',
        text: title,
        content: (
          <Notification
            imageUrl={imageUrl}
            title={title}
            message={message}
            isOffer={isOffer}
            chatId={chatId}
            onClick={() => {
              toastApi.hide(notificationId);
            }}
          />
        ),
        hideDelay: 6000,
        leftAdornment: null,
        rightAdornment: null,
      });
    });

    dispatch(clearNotifications());
  }, [notifications, toastApi, dispatch, telemetry]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        dispatch(closeEventChannel());
      } else if (isWSEnabled) {
        dispatch(openEventChannel());
      }
    };

    if (isWSEnabled) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (isWSEnabled) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [dispatch, isWSEnabled]);

  return null;
}
